/* STORES */
import Echo from 'laravel-echo';

import { HttpStatus } from '@/enums/httpStatus';
import { useGlobalStore } from '@/stores/global';

import ViktorClient from '../viktorClient/ViktorClient';

type Reverb = {
  getInstance: () => Echo<'reverb'>;
  addListener: ({
    channel,
    event,
    name,
    callback
  }: {
    channel: string;
    event: string;
    name: string;
    callback: (any) => void;
  }) => void;
  removeListener: ({ channel, event, name }: { channel: string; event: string; name: string }) => void;
};

const ReverbClient = (() => {
  /* Instance of client that includes all methods */
  let instance;

  /* Echo instance */
  let echo: Echo<'reverb'>;
  const listeners = {};

  const constructor = function () {
    /**
     * Allows to get the current echo instance
     */
    this.getInstance = function (): Echo<'reverb'> {
      return echo;
    };

    /**
     * Function called on init to configure the echo instance
     */
    this.init = function () {
      const globalStore = useGlobalStore();
      echo = new Echo({
        broadcaster: 'reverb',
        key: globalStore.getPusher.key,
        wsHost: globalStore.getReverbUrl,
        channelAuthorization: {
          endpoint: `${globalStore.getAPIURL}/broadcasting/auth`,
          transport: 'jsonp',
          customHandler: (params, cb) => {
            ViktorClient()
              .post(`/broadcasting/auth`, {
                socket_id: params.socketId,
                channel_name: params.channelName
              })
              .then(response => {
                cb(null, { auth: response.auth });
              })
              .catch(error => {
                if (error.status === HttpStatus.UNAUTHORIZED) {
                  echo.leaveAllChannels();
                }
              });
          }
        },
        wsPort: 80,
        wssPort: 443,
        forceTLS: true,
        enabledTransports: ['ws', 'wss']
      });
    };

    // add event listener - custom wrapper in order to keep track of listeners and be able to stop listening to specific listeners
    this.addListener = function ({ channel, event, name, callback }) {
      const eventName = `${channel}.${event}.${name}`;
      if (!listeners[eventName]) {
        listeners[eventName] = callback;
        echo.private(channel).listen(event, callback);
      }
    };

    // remove listener - allows to remove one specific listener from the registered listeners without impacting other listeners on the same event
    this.removeListener = function ({ channel, event, name }) {
      const eventName = `${channel}.${event}.${name}`;
      if (listeners[eventName]) {
        echo.private(channel).stopListening(event, listeners[eventName]);
        delete listeners[eventName];
      }
    };
  };

  /**
   * Create  instance
   */
  const createInstance = () => {
    instance = new constructor();
    instance.init();
    instance.constructor = null;
    return instance;
  };

  return (): Reverb => {
    // Check if instance already exists
    const echoInstance = instance || createInstance();
    return echoInstance;
  };
})();

export default ReverbClient;
